import React, { useState, useRef } from 'react';
import Select from 'react-select';
import { useTranslation } from '../hooks/useTranslation';

const AddCategoryCard = ({ categoriesOption, onAddCategory }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [inputErrorMessage, setInputErrorMessage] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [newCatImage, setNewCatImage] = useState("");
  const inputImage = useRef(null);
  const { t } = useTranslation();

  const resetInputValues = () => {
    setNewCatName("");
    setNewCatImage("");
    setInputErrorMessage("");
    if (inputImage.current) {
      inputImage.current.value = "";
    }
  };

  const handleFlip = () => {
    if (!isFlipped) {
      setIsFlipped(true);
      resetInputValues();
    }
  };

  const handleCancel = () => {
    setIsFlipped(false);
    resetInputValues();
  };

  const handleSubmit = async () => {
    try {
      if (!newCatName) {
        setInputErrorMessage("Välj en kategori.");
        return;
      }

      const newCategory = {
        name: newCatName,
        image_url: newCatImage,
      };

      await onAddCategory(newCategory);
      setIsFlipped(false);
      resetInputValues();
    } catch (error) {
      console.error("Error Details:", error);
      if (error.response) {
        setInputErrorMessage(
          error.response.data.message || "Serverfel inträffade."
        );
      } else if (error.request) {
        setInputErrorMessage("Kunde inte ansluta till servern.");
      } else {
        setInputErrorMessage(`Ett oväntat fel inträffade: ${error.message}`);
      }
    }
  };

  return (
    <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border-2 border-dashed border-gray-300 overflow-hidden hover:shadow-md transition-all duration-300 transform hover:scale-[1.02] group" style={{ height: '300px' }}>
      {/* Card Container with 3D flip effect */}
      <div className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
        
        {/* Front Side - Add Button */}
        <div 
          onClick={handleFlip}
          className="absolute inset-0 w-full h-full cursor-pointer backface-hidden"
        >
          <div className="relative h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mobile:w-20 mobile:h-20 tablet:w-20 tablet:h-20 laptop:w-20 laptop:h-20 desktop:w-20 desktop:h-20 bg-gradient-to-br from-secondary-green/20 to-secondary-green/30 rounded-full flex items-center justify-center mx-auto mb-3 mobile:mb-4 tablet:mb-4 laptop:mb-4 desktop:mb-4 group-hover:scale-110 transition-transform duration-300">
                <img src="/assets/icons/Plus.png" alt="Add category" className="w-8 h-8 mobile:w-10 mobile:h-10 tablet:w-10 tablet:h-10 laptop:w-10 laptop:h-10 desktop:w-10 desktop:h-10 text-secondary-green" />
              </div>
              <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-600">{t('Lägg till ny kategori')}</p>
            </div>
          </div>
        </div>

        {/* Back Side - Form */}
        <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180">
          <div className="h-full flex flex-col bg-white">
            {/* Form Header */}
            <div className="flex-shrink-0 p-2 mobile:p-3 tablet:p-3 laptop:p-3 desktop:p-3 bg-gradient-to-br from-gray-50 to-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-bold text-gray-900">
                  {t('Skapa ny kategori')}
                </h3>
                <button
                  onClick={handleCancel}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <img src="/assets/icons/Dolj.png" alt="Close" className="w-3 h-3 text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Form Content */}
            <div className="flex-1 p-2 mobile:p-3 tablet:p-3 laptop:p-3 desktop:p-3 space-y-2 mobile:space-y-2 tablet:space-y-2 laptop:space-y-2 desktop:space-y-2 overflow-y-auto">
              {/* Category Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('Kategori')}
                </label>
                <Select
                  options={categoriesOption}
                  onChange={(selected) => {
                    setNewCatName(selected.label);
                    setNewCatImage(selected.image || "");
                    setInputErrorMessage("");
                  }}
                  placeholder={t('Välj en kategori')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      minHeight: '28px',
                      fontSize: '11px',
                      '@media (min-width: 640px)': {
                        fontSize: '12px',
                        minHeight: '32px',
                      }
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '1px 6px',
                      '@media (min-width: 640px)': {
                        padding: '2px 8px',
                      }
                    }),
                    input: (base) => ({
                      ...base,
                      margin: '0px',
                      padding: '0px',
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: '28px',
                      '@media (min-width: 640px)': {
                        height: '32px',
                      }
                    }),
                  }}
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('Bild URL (valfritt)')}
                </label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent text-xs"
                  type="text"
                  ref={inputImage}
                  value={newCatImage}
                  onChange={(e) => {
                    setNewCatImage(e.target.value);
                    setInputErrorMessage("");
                  }}
                  placeholder=""
                />
              </div>

              {/* Error Message */}
              {inputErrorMessage && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-xs">{inputErrorMessage}</p>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex-shrink-0 p-2 mobile:p-3 tablet:p-3 laptop:p-3 desktop:p-3 border-t border-gray-200">
              <div className="flex space-x-2">
                <button
                  className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 bg-secondary-green text-white font-semibold rounded-lg hover:opacity-90 transition-opacity text-xs"
                  onClick={handleSubmit}
                >
                  <img
                    src="/assets/icons/Check.png"
                    alt="Create"
                    className="w-3 h-3"
                  />
                  {t('Skapa')}
                </button>
                <button
                  className="flex-1 px-2 py-1 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-xs"
                  onClick={handleCancel}
                >
                  {t('Avbryt')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCategoryCard;
